/**
 * 📋 SDLC 6.3.1 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/components/dashboard/DataTable.tsx
 * PURPOSE: Sprint 34 - Reusable Dashboard Data Table Component with Sorting
 * SDLC FRAMEWORK VERSION: 6.1.1 (7-Pillar + 3-Section + Long-Running Agent Protocol)
 * DATE: 2026-01-26
 * COMPLIANCE: SDLC 6.3.1 + NQH Brand Design System V2.1 + WCAG 2.1 AA
 * PILLAR: P1 (Stage 06 - BUILD) + P2 (Rule 4: Tier-3 Code Review) + P6 (UI/UX Design)
 * SPRINT GOVERNANCE: Rule 3: Zero-Mock Tolerance, Rule 9: Documentation Standards
 *
 * @business-context
 * - Domain: Vietnamese F&B Dashboard Data Display, Tabular Reports
 * - Impact: Data Presentation, User Experience, Reporting Consistency
 * - Stakeholders: Dashboard Users, Operations Team, CFO, Frontend Team
 *
 * @technical-architecture
 * - Layer: Frontend Presentation Layer, Shared Component Library
 * - Pattern: Controlled Component, Generics, CSS Modules, Accessibility
 * - Dependencies: React 18, CSS Modules, NQH Design Tokens
 *
 * @cross-references
 * - Design: docs/02-design/04-ui-ux-design/NQH-BRAND-DESIGN-SYSTEM.md
 * - Sprint Plan: docs/08-collaborate/06-Project-Management/SPRINT-34-EXECUTION-PLAN.md
 * - CSS Module: frontend/src/components/dashboard/DataTable.module.css
 * - Related: frontend/src/components/dashboard/DashboardCard.tsx
 */

import React from 'react';
import { cn } from '@/utils/cn';
import styles from './DataTable.module.css';

/**
 * Column Definition Interface
 *
 * @interface ColumnDef
 * @property {string} key - Unique column key
 * @property {string} label - Column header label
 * @property {(value: any, row: T) => React.ReactNode} [render] - Custom cell renderer
 * @property {boolean} [sortable] - Enable sorting for this column
 * @property {'left' | 'center' | 'right'} [align] - Text alignment (default: 'left')
 * @property {string} [width] - Column width (e.g., '100px', '20%', 'auto')
 */
export interface ColumnDef<T = any> {
  key: string;
  label: string;
  render?: (value: any, row: T) => React.ReactNode;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

/**
 * Sort Direction Type
 */
export type SortDirection = 'asc' | 'desc' | null;

/**
 * Data Table Props Interface
 *
 * @interface DataTableProps
 * @property {T[]} data - Table data array
 * @property {ColumnDef<T>[]} columns - Column definitions
 * @property {string} [sortKey] - Currently sorted column key
 * @property {SortDirection} [sortDirection] - Current sort direction
 * @property {(key: string) => void} [onSort] - Sort handler callback
 * @property {boolean} [isLoading] - Loading state
 * @property {string} [emptyMessage] - Empty state message
 * @property {string} [className] - Additional CSS classes
 * @property {boolean} [striped] - Striped rows (default: true)
 * @property {boolean} [hoverable] - Row hover effect (default: true)
 */
export interface DataTableProps<T = any> {
  data: T[];
  columns: ColumnDef<T>[];
  sortKey?: string;
  sortDirection?: SortDirection;
  onSort?: (key: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  className?: string;
  striped?: boolean;
  hoverable?: boolean;
}

/**
 * DataTable Component
 *
 * Reusable data table component for dashboard data display with sorting support.
 * Generic type-safe table with Vietnamese F&B design (Sprint 34 Story 1).
 *
 * **Features:**
 * - Type-safe with TypeScript generics
 * - Sortable columns with visual indicators (↑ ↓)
 * - Custom cell renderers
 * - Loading and empty states
 * - Striped rows and hover effects
 * - Responsive design (horizontal scroll on mobile)
 * - WCAG 2.1 AA compliant (role="table", aria-sort, aria-label)
 * - 100% design token compliance
 * - Vietnamese Coffee accent for sortable headers
 *
 * **Design Tokens:**
 * - Background: var(--nqh-gray-900), var(--nqh-gray-800)
 * - Border: var(--nqh-gray-800), var(--nqh-gray-700)
 * - Hover: rgba(217, 119, 6, 0.1) - Coffee tint
 * - Active sort: var(--nqh-coffee-400)
 *
 * @example
 * ```tsx
 * interface Employee {
 *   id: number;
 *   name: string;
 *   branch: string;
 *   hours: number;
 * }
 *
 * const columns: ColumnDef<Employee>[] = [
 *   { key: 'name', label: 'Employee', sortable: true },
 *   { key: 'branch', label: 'Branch', sortable: true },
 *   {
 *     key: 'hours',
 *     label: 'Hours',
 *     sortable: true,
 *     align: 'right',
 *     render: (value) => `${value}h`,
 *   },
 * ];
 *
 * <DataTable
 *   data={employees}
 *   columns={columns}
 *   sortKey="name"
 *   sortDirection="asc"
 *   onSort={(key) => handleSort(key)}
 *   emptyMessage="No employees found"
 * />
 * ```
 *
 * @param props - DataTableProps
 * @returns Accessible data table component
 */
export function DataTable<T = any>({
  data,
  columns,
  sortKey,
  sortDirection,
  onSort,
  isLoading = false,
  emptyMessage = 'No data available',
  className,
  striped = true,
  hoverable = true,
}: DataTableProps<T>) {
  /**
   * Handle column header click for sorting
   */
  const handleSort = (columnKey: string) => {
    if (onSort) {
      onSort(columnKey);
    }
  };

  /**
   * Get cell value from row data
   */
  const getCellValue = (row: T, columnKey: string): any => {
    return (row as any)[columnKey];
  };

  return (
    <div className={cn(styles.tableContainer, className)}>
      <div className={styles.tableWrapper}>
        <table className={styles.table} role="table">
          <thead className={styles.thead}>
            <tr role="row">
              {columns.map((column) => {
                const isSorted = sortKey === column.key;
                const ariaSortValue = isSorted
                  ? sortDirection === 'asc'
                    ? 'ascending'
                    : sortDirection === 'desc'
                    ? 'descending'
                    : 'none'
                  : undefined;

                return (
                  <th
                    key={column.key}
                    className={cn(
                      styles.th,
                      styles[`align-${column.align || 'left'}`],
                      column.sortable && styles.sortable,
                      isSorted && styles.sorted
                    )}
                    style={column.width ? { width: column.width } : undefined}
                    aria-sort={ariaSortValue}
                    role="columnheader"
                  >
                    {column.sortable && onSort ? (
                      <button
                        type="button"
                        className={styles.sortButton}
                        onClick={() => handleSort(column.key)}
                        aria-label={`Sort by ${column.label}`}
                      >
                        <span>{column.label}</span>
                        <span className={styles.sortIcon} aria-hidden="true">
                          {isSorted ? (
                            sortDirection === 'asc' ? (
                              '↑'
                            ) : (
                              '↓'
                            )
                          ) : (
                            '↕'
                          )}
                        </span>
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody className={styles.tbody}>
            {isLoading ? (
              <tr role="row">
                <td
                  colSpan={columns.length}
                  className={styles.emptyCell}
                  role="cell"
                >
                  <span className={styles.loading} aria-live="polite">
                    Loading...
                  </span>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr role="row">
                <td
                  colSpan={columns.length}
                  className={styles.emptyCell}
                  role="cell"
                >
                  <span className={styles.empty} aria-live="polite">
                    {emptyMessage}
                  </span>
                </td>
              </tr>
            ) : (
              data.map((row, rowIndex) => (
                <tr
                  key={rowIndex}
                  className={cn(
                    styles.tr,
                    striped && rowIndex % 2 === 1 && styles.striped,
                    hoverable && styles.hoverable
                  )}
                  role="row"
                >
                  {columns.map((column) => {
                    const value = getCellValue(row, column.key);
                    const cellContent = column.render
                      ? column.render(value, row)
                      : value;

                    return (
                      <td
                        key={column.key}
                        className={cn(
                          styles.td,
                          styles[`align-${column.align || 'left'}`]
                        )}
                        role="cell"
                      >
                        {cellContent}
                      </td>
                    );
                  })}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default DataTable;
